import { AlertDialog, Loading, ThreeDots } from '@components/ui';
import { CheckCircle, HourglassEmpty } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { Button, Tooltip } from '@mui/material';
import MUIDataTable, { MUIDataTableOptions, MUIDataTableProps } from 'mui-datatables';

import { useSnackbar } from '@app/hooks/useSnackbar';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { ThreeDotItem } from '@components/ui/ThreeDots/ThreeDots.tsx';
import { UserRoleChip } from '@components/ui/UserRoleChip/UserRoleChip';
import { CompanyUsersSortField, useCompanyUsersQuery } from '@dieterApi/user/useCompanyUsersQuery';
import { useAssignExistingUserMutation } from '@dieterApi/user/useUserAssignExistingMutation';
import { useUserDeleteMutation } from '@dieterApi/user/useUserDeleteMutation';
import { useInviteUserMutation } from '@dieterApi/user/useUserInviteMutation';
import { UserRole } from '@dieterApi/user/useUserQuery';
import { useUpdateUserMutation } from '@dieterApi/user/useUserUpdateMutation';
import BadgeIcon from '@mui/icons-material/Badge';
import Cancel from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { DateTime } from 'luxon';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';
import { useAsyncCallback } from 'react-async-hook';
import { Trans, useTranslation } from 'react-i18next';
import UserManagementCreateModal from './UserManagementCreateModal';
import { UserManagementDpoModal } from './UserManagementDpoModal';
import UserManagementUpdateModal from './UserManagementUpdateModal';

function UserManagementTable() {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUserWithCompany();

  // Two management tiers:
  //  - Admin: full access across all tenants.
  //  - Customer manager: an Editor on a DpoManaged tenant may manage users of
  //    their own Mandanten with limited roles.
  const isAdmin = user?.role === UserRole.Admin;
  const isCustomerManager = user?.role === UserRole.Editor && (user?.hasFeature('DpoManaged') ?? false);
  const canManage = isAdmin || isCustomerManager;
  const canEditDpoData = user?.role === UserRole.Admin || user?.role === UserRole.Dpo;

  // Server-side pagination / search / sort state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<CompanyUsersSortField>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const {
    data: usersData,
    previousData,
    loading,
    refetch,
  } = useCompanyUsersQuery({
    page: page + 1, // MUI uses 0-based, our API uses 1-based
    pageSize,
    search: search || undefined,
    sortBy,
    sortOrder,
  });

  // Keep the last successful result on screen while a new query (e.g. a search or
  // page change) is in flight. Otherwise the table would unmount and the search
  // input would lose focus mid-typing.
  const effectiveData = usersData ?? previousData;
  const users = effectiveData?.companyUsers.users || [];
  const total = effectiveData?.companyUsers.total || 0;
  const totalCompanies = effectiveData?.companyUsers.totalCompanies ?? 0;

  // Debounce the search so we don't fire a query on every keystroke.
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
        setPage(0);
      }, 400),
    []
  );
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<any[] | null>(null);
  const [openUpdateModal, setOpenUpdateModal] = useState<any[] | null>(null);
  const [openDpoModal, setOpenDpoModal] = useState<any | null>(null);
  // Set when an invite hit an already-registered email; drives the dialog that
  // offers assigning the selected Mandanten to that existing user instead.
  const [pendingAssign, setPendingAssign] = useState<{ email: string; enabledCompanies: string[] } | null>(null);

  const [inviteUser] = useInviteUserMutation();
  const [assignExistingUser] = useAssignExistingUserMutation();
  const [updateUser] = useUpdateUserMutation();
  const [deleteUser] = useUserDeleteMutation();

  const handleAdd = useAsyncCallback(async (values: { email: string; role: UserRole; enabledCompanies: string[] }) => {
    inviteUser({
      variables: {
        email: values.email,
        role: values.role,
        enabledCompanies: values.enabledCompanies,
      },
    })
      .then(() => {
        enqueueSnackbar(t('route.user-management.invite-success'), { variant: 'success' });
        setOpenCreateModal(false);
        refetch();
      })
      .catch((err) => {
        const alreadyExists = err.graphQLErrors?.some(
          (e: { extensions?: { code?: string } }) => e.extensions?.code === 'USER_ALREADY_EXISTS'
        );
        if (alreadyExists) {
          setPendingAssign({ email: values.email, enabledCompanies: values.enabledCompanies });
        } else {
          enqueueSnackbar(err.message, { variant: 'error' });
        }
      });
  });

  const handleAssignExisting = useAsyncCallback(async () => {
    if (!pendingAssign) return;
    assignExistingUser({ variables: pendingAssign })
      .then(() => {
        enqueueSnackbar(t('route.user-management.assign-existing.success'), { variant: 'success' });
        setOpenCreateModal(false);
        refetch();
      })
      .catch((err) => {
        enqueueSnackbar(err.message, { variant: 'error' });
      });
  });

  const handleEdit = useAsyncCallback(async (role: UserRole, enabledCompanies: string[]) => {
    updateUser({
      variables: {
        userId: openUpdateModal?.[0],
        role,
        enabledCompanies,
      },
    })
      .then(() => {
        enqueueSnackbar(t('route.user-management.update-success'), { variant: 'success' });
        setOpenUpdateModal(null);
        refetch();
      })
      .catch((err) => {
        enqueueSnackbar(err.message, { variant: 'error' });
      });
  });
  const handleDelete = useAsyncCallback(async () => {
    deleteUser({
      variables: {
        userId: openDeleteModal?.[0],
      },
    })
      .then(() => {
        enqueueSnackbar(t('route.user-management.delete-success'), { variant: 'success' });
        refetch();
      })
      .catch((err) => {
        enqueueSnackbar(err.message, { variant: 'error' });
      });

    setOpenDeleteModal(null);
  });

  const columns: MUIDataTableProps['columns'] = [
    {
      name: 'id',
      label: 'ID',
      options: { display: 'excluded', sort: false },
    },
    {
      name: 'email',
      label: t('route.user-management.table.header.email', 'Email'),
    },
    {
      name: 'role',
      label: t('route.user-management.table.header.role', 'Role'),
      options: {
        customBodyRender: (value: UserRole) => {
          return <UserRoleChip role={value} />;
        },
      },
    },
    {
      name: 'companies',
      label: t('route.user-management.table.header.activecompanies', 'Aktive Mandanten'),
      options: {
        sort: false,
        customBodyRender: (value: { id: string; name: string }[]) => {
          const hasAll = totalCompanies > 0 && value.length >= totalCompanies;
          return hasAll ? t('common.all') : value.map((c) => c.name).join(', ');
        },
      },
    },
    {
      name: 'confirmed',
      label: t('route.user-management.table.header.inviteStatus', 'Invite Status'),
      options: {
        customBodyRender: (value: boolean) => {
          const opts = (
            {
              pending: {
                label: t('route.user-management.inviteStatus.Pending', 'Pending'),
                hint: t('route.user-management.invite-status-hint.pending', 'waiting for user to accept the invite'),
                icon: HourglassEmpty,
              },
              active: {
                label: t('route.user-management.inviteStatus.Active', 'Active'),
                hint: t('route.user-management.invite-status-hint.active', 'user has already accepted the invite'),
                icon: CheckCircle,
              },
            } as const
          )[value ? 'active' : 'pending'];

          return (
            <Tooltip enterTouchDelay={0} leaveTouchDelay={5000} title={opts.hint} placement="bottom">
              <div className="inline-flex items-center gap-1 cursor-help">
                <opts.icon fontSize="small" color={value ? 'success' : 'warning'} />
                <span className="font-medium underline text-sm">{opts.label}</span>
              </div>
            </Tooltip>
          );
        },
      },
    },
    {
      name: 'mfaEnabled',
      label: 'MFA',
      options: {
        customBodyRender: (value: boolean) => {
          const opts = (
            {
              disabled: {
                label: t('common.inactive'),
                hint: t('route.user-management.mfa.inactive'),
                icon: Cancel,
              },
              enabled: {
                label: t('route.user-management.inviteStatus.Active', 'Active'),
                hint: t('route.user-management.mfa.active'),
                icon: CheckCircle,
              },
            } as const
          )[value ? 'enabled' : 'disabled'];

          return (
            <Tooltip enterTouchDelay={0} leaveTouchDelay={5000} title={opts.hint} placement="bottom">
              <div className="inline-flex items-center gap-1 cursor-help">
                <opts.icon fontSize="small" color={value ? 'success' : 'warning'} />
                <span className="font-medium underline text-sm">{opts.label}</span>
              </div>
            </Tooltip>
          );
        },
      },
    },
    {
      name: 'lastLogin',
      label: t('route.user-management.table.header.lastLogin', 'Last Login'),
      options: {
        customBodyRender: (value: string) =>
          value ? DateTime.fromJSDate(new Date(value)).setLocale(i18n.language.slice(0, 2)).toRelative() : '-',
      },
    },
    {
      name: 'createdAt',
      label: t('route.user-management.table.header.createdAt', 'Created At'),
      options: {
        customBodyRender: (value) => {
          return new Date(value).toLocaleString();
        },
      },
    },
    {
      name: 'actions',
      label: t('route.user-management.table.header.actions', 'Aktionen'),
      options: {
        sort: false,
        customHeadLabelRender: () => '',
        customBodyRender: (value, tableMeta) => {
          const userId = tableMeta.rowData[0];
          const userData = users.find((u) => u.id === userId);
          const isCurrentUser = user?.id === userId;
          const isTargetAdmin = userData?.role === UserRole.Admin;
          const isTargetDpo = userData?.role === UserRole.Dpo;
          const targetIsElevated = isTargetAdmin || isTargetDpo;
          // Admin may manage anyone; a customer manager may only manage
          // non-elevated (Editor / ReadOnly) users.
          const canManageTarget = !isCurrentUser && (isAdmin || (isCustomerManager && !targetIsElevated));

          const items: ThreeDotItem[] = [];

          // Edit role/companies
          if (canManageTarget) {
            items.push({
              label: (
                <div className="flex gap-1 items-center">
                  <EditIcon fontSize="inherit" />
                  <span className="grow">{t('common.edit')}</span>
                </div>
              ),
              onClick: () => setOpenUpdateModal(tableMeta.rowData),
            });
          }

          // Show DPO edit for elevated users, but only to Admin / Dpo who own
          // the DpoManaged feature.
          if (targetIsElevated && user?.hasFeature('DpoManaged') && canEditDpoData) {
            items.push({
              label: (
                <div className="flex gap-1 items-center">
                  <BadgeIcon fontSize="inherit" />
                  <span className="grow">{t('route.user-management.edit-dpo', 'DSB-Daten bearbeiten')}</span>
                </div>
              ),
              onClick: () => setOpenDpoModal(userData),
            });
          }

          // Delete
          if (canManageTarget) {
            items.push({
              label: (
                <div className="flex gap-1 items-center">
                  <DeleteIcon fontSize="inherit" />
                  <span className="grow">{t('common.delete')}</span>
                </div>
              ),
              onClick: () => setOpenDeleteModal(tableMeta.rowData),
            });
          }

          // If no items, don't show the menu
          if (items.length === 0) return null;

          return <ThreeDots items={items} />;
        },
      },
    },
  ];
  const options: MUIDataTableOptions = {
    customToolbar: () => (
      <Button
        variant="outlined"
        onClick={() => setOpenCreateModal(true)}
        startIcon={<AddIcon />}
        disabled={!canManage}
        size="small"
      >
        {t('common.add', 'hinzufügen')}
      </Button>
    ),
    serverSide: true,
    count: total,
    page,
    rowsPerPage: pageSize,
    rowsPerPageOptions: [10, 25, 50, 100],
    sortOrder: { name: sortBy, direction: sortOrder },
    onTableChange: (action, tableState) => {
      switch (action) {
        case 'changePage':
          setPage(tableState.page);
          break;
        case 'changeRowsPerPage':
          setPageSize(tableState.rowsPerPage);
          setPage(0);
          break;
        case 'search':
          debouncedSetSearch(tableState.searchText || '');
          break;
        case 'sort':
          if (tableState.sortOrder && tableState.sortOrder.name) {
            setSortBy(tableState.sortOrder.name as CompanyUsersSortField);
            setSortOrder(tableState.sortOrder.direction as 'asc' | 'desc');
          }
          break;
      }
    },
    download: false,
    print: false,
    elevation: 0,
    selectableRows: 'none',
    selectableRowsHeader: false,
    search: true,
    filter: false,
    viewColumns: false,
    pagination: true,
    textLabels: {
      body: {
        noMatch: loading
          ? t('common.loading', 'Laden...')
          : t('route.user-management.table.no-users', 'Keine Benutzer gefunden'),
      },
    },
  };

  const tableData = users.map((u) => ({
    id: u.id,
    email: u.email,
    role: u.role,
    companies: u.companies,
    confirmed: u.confirmed,
    mfaEnabled: u.mfaEnabled,
    lastLogin: u.lastLogin,
    createdAt: u.createdAt,
    actions: null,
  }));

  return (
    <>
      <div>
        {effectiveData ? <MUIDataTable columns={columns} data={tableData} title="" options={options} /> : <Loading />}
      </div>
      <UserManagementCreateModal
        open={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        onSubmit={handleAdd.execute}
      />
      {openUpdateModal && (
        <UserManagementUpdateModal
          open={!!openUpdateModal}
          userValues={openUpdateModal}
          onClose={() => setOpenUpdateModal(null)}
          onSubmit={handleEdit.execute}
        />
      )}
      {openDpoModal && (
        <UserManagementDpoModal
          open={!!openDpoModal}
          onClose={() => setOpenDpoModal(null)}
          userId={openDpoModal.id}
          initialValues={{
            dpoName: openDpoModal.dpoName,
            dpoEmail: openDpoModal.dpoEmail,
            dpoPhone: openDpoModal.dpoPhone,
            dpoAddress: openDpoModal.dpoAddress,
            dpoCompany: openDpoModal.dpoCompany,
            dpoAvatarUrl: openDpoModal.dpoAvatarUrl,
          }}
        />
      )}
      <AlertDialog
        open={!!pendingAssign}
        setOpen={(open) => {
          if (!open) setPendingAssign(null);
        }}
        title={t('route.user-management.assign-existing.title', 'Benutzer existiert bereits')}
        message={
          <Trans
            t={t}
            i18nKey="route.user-management.assign-existing.message"
            values={{ email: pendingAssign?.email }}
          >
            Die E-Mail-Adresse{' '}
            <b>
              {{
                // @ts-ignore
                email: pendingAssign?.email,
              }}
            </b>{' '}
            ist bereits registriert. Möchten Sie diesem Benutzer stattdessen Zugriff auf die ausgewählten Mandanten
            geben? Rolle und bestehende Zuordnungen bleiben unverändert.
          </Trans>
        }
        acceptText={t('route.user-management.assign-existing.confirm', 'Zuordnen')}
        onAccept={handleAssignExisting.execute}
      />
      <AlertDialog
        open={!!openDeleteModal}
        setOpen={() => setOpenDeleteModal(null)}
        message={
          <Trans
            t={t}
            i18nKey="route.user-management.delete-confirmation-message"
            values={{ email: openDeleteModal?.[1] }}
          >
            Möchten Sie diesen Benutzer{' '}
            <b>
              {{
                // @ts-ignore
                email: openDeleteModal?.[1],
              }}
            </b>{' '}
            wirklich löschen?
          </Trans>
        }
        onAccept={handleDelete.execute}
      />
    </>
  );
}

export default UserManagementTable;
